import { useCallback, useMemo, useState } from "react";
import { useCostAdjustments, useSaveCostAdjustmentsMutation } from "../../../api_utils/requests";
import type { CostAdjustment, CostAdjustmentEditable } from "../../../api_utils/types";
import { calculateCostAdjustmentAmount, formatCostAdjustmentAmount } from "./calculateCostAdjustmentAmount";

export function useEditableCostAdjustments(
  purchaseOrderId: number,
  lineTotal: number,
): {
  costAdjustments: CostAdjustmentEditable[];
  setCostAdjustments: (costAdjustments: CostAdjustmentEditable[]) => void;
  total: number;
  isLoading: boolean;
  isSaving: boolean;
  save: () => Promise<void>;
  discard: () => void;
} {
  const { data, isPlaceholderData } = useCostAdjustments(purchaseOrderId);
  const { mutateAsync, isPending } = useSaveCostAdjustmentsMutation();
  const [editedCostAdjustments, setEditedCostAdjustments] = useState<CostAdjustmentEditable[] | null>(null);
  const savedCostAdjustments = useMemo(() => formatClientSide(data.cost_adjustments), [data]);
  const costAdjustments = editedCostAdjustments ?? savedCostAdjustments;
  const total = costAdjustments.reduce(
    (sum, costAdjustment) => sum + calculateCostAdjustmentAmount(costAdjustment, lineTotal),
    0,
  );

  const save = useCallback(async (): Promise<void> => {
    await mutateAsync({
      purchase_order_id: purchaseOrderId,
      cost_adjustments: costAdjustments.map((costAdjustment) => ({
        ...costAdjustment,
        amount: formatCostAdjustmentAmount(costAdjustment),
      })),
    });
    setEditedCostAdjustments(null);
  }, [costAdjustments, mutateAsync, purchaseOrderId]);

  const discard = useCallback((): void => {
    setEditedCostAdjustments(null);
  }, []);

  return {
    costAdjustments,
    setCostAdjustments: setEditedCostAdjustments,
    total,
    isLoading: isPlaceholderData,
    isSaving: isPending,
    save,
    discard,
  };
}

const formatClientSide = (costAdjustments: CostAdjustment[]): CostAdjustmentEditable[] =>
  costAdjustments.map((costAdjustment) => ({
    ...costAdjustment,
    amount: costAdjustment.amount.toString(),
  }));
